// Store references to our event handlers so we can remove them.
/** @type {Record<string, (event: Event) => void>} */
const trapFocusHandlers = {};

/**
 * Get all focusable elements within a container.
 * @param {HTMLElement} container - The container to get focusable elements from.
 * @returns {HTMLElement[]} An array of focusable elements.
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

/**
 * Whether a control is on show (not inside display:none or visibility:hidden).
 * @param {HTMLElement} element
 * @returns {boolean}
 */
function isShown(element) {
  if (typeof element.checkVisibility === 'function') return element.checkVisibility({ visibilityProperty: true });
  return element.getClientRects().length > 0 && getComputedStyle(element).visibility !== 'hidden';
}

/**
 * Trap focus within the given container.
 * @param {HTMLElement} container - The container to trap focus within.
 */
export function trapFocus(container) {
  // Clean up any previously set traps.
  removeTrapFocus();

  // Gather focusable elements.
  const focusable = getFocusableElements(container);
  if (!focusable.length) {
    // If nothing is focusable, just abort—no need to trap.
    return;
  }

  const first = focusable[0];

  // Keydown handler for cycling focus with Tab and Shift+Tab.
  // The list is read again on every key, keeping only controls that are on show, and the move is made
  // here rather than left to the browser: in WebKit a Shift+Tab during a drawer's slide-in used to land
  // on the announcement bar or <body> (round 3 cross-browser report #38).
  /** @type {(event: KeyboardEvent) => void} */
  trapFocusHandlers.keydown = (event) => {
    if (event.key !== 'Tab') return;

    const shown = getFocusableElements(container).filter(isShown);
    const items = shown.length ? shown : focusable;
    const activeEl = /** @type {HTMLElement | null} */ (document.activeElement);
    const index = activeEl ? items.indexOf(activeEl) : -1;

    // Inside the container on a control the browser can step from normally: let it, unless this is an edge.
    if (index > 0 && index < items.length - 1) return;

    event.preventDefault();
    if (index === -1) {
      (event.shiftKey ? items[items.length - 1] : items[0])?.focus();
    } else if (event.shiftKey) {
      (index === 0 ? items[items.length - 1] : items[index - 1])?.focus();
    } else {
      (index === items.length - 1 ? items[0] : items[index + 1])?.focus();
    }
  };

  // Focusin (capturing) handler to forcibly keep focus in the container
  /** @type {(event: FocusEvent) => void} */
  trapFocusHandlers.focusin = (event) => {
    // If the newly focused element isn't inside the container, redirect focus back.
    if (event.target instanceof Node && !container.contains(event.target)) {
      event.stopPropagation();
      // E.g., refocus the first focusable element:
      first?.focus();
    }
  };

  // Attach the handlers
  document.addEventListener('keydown', trapFocusHandlers.keydown, true);
  // Use capture phase for focusin so we can catch it before it lands outside
  document.addEventListener('focusin', trapFocusHandlers.focusin, true);

  // Finally, put focus where you want it.
  container.focus();
}

/**
 * Remove focus trap and optionally refocus another element.
 */
export function removeTrapFocus() {
  trapFocusHandlers.keydown && document.removeEventListener('keydown', trapFocusHandlers.keydown, true);
  trapFocusHandlers.focusin && document.removeEventListener('focusin', trapFocusHandlers.focusin, true);
}

/**
 * Cycle focus to the next or previous link
 *
 * @param {HTMLElement[]} items
 * @param {number} increment
 */
export function cycleFocus(items, increment) {
  const currentIndex = items.findIndex((item) => item.matches(':focus'));
  let targetIndex = currentIndex + increment;

  if (targetIndex >= items.length) {
    targetIndex = 0;
  } else if (targetIndex < 0) {
    targetIndex = items.length - 1;
  }

  const targetItem = items[targetIndex];

  if (!targetItem) return;

  targetItem.focus();
}
